/**
 * TAG PUSHER PAGE (Admin Tool)
 *
 * Adds a value to a chosen field on user items across the app. The
 * scope is picked in Step 1 (item type + brand + optional specific
 * polish), the payload in Step 2 (value + target field). Optionally,
 * a substring filter can narrow within the scope. Non-destructive by
 * default: item names are never modified, only the chosen field on
 * each matching item gets the new value appended (deduped).
 *
 * Scope-first flow:
 *   1. Pick the item type (polish, plate, etc.). Default: polish.
 *   2. Type a brand and click Load. When item type is polish, we
 *      query polishCatalog for that brand and populate a polish
 *      picker so you can scope to ONE specific polish (safer than
 *      substring matching). Leave the picker on "All polishes in
 *      this brand" for a brand-wide push.
 *   3. Optionally, add a substring filter to narrow within the
 *      picked scope (useful for the "All polishes" mode).
 *
 * Target field options:
 *   - `tags[]` — generic tag array (default; works for all itemTypes).
 *   - `finish[]` — polish only. Where magnetic/holo/thermal etc.
 *     actually belong.
 *   - `specialFeatures[]` — polish only. Quick dry, glow, etc.
 *   - `holoType[]` — polish only. Linear, scattered.
 *   - `baseType` — polish only, single string. Creme, jelly, sheer,
 *     crelly. Only fills in when currently empty; never overwrites
 *     a user-assigned baseType.
 *
 * The "Also remove from tags[]" toggle handles the migration case:
 * if you previously pushed "Magnetic" into tags[] but it belongs in
 * finish[], re-run with target = Finish and the toggle on.
 *
 * Only user items (users/{uid}/items/*), never brand catalog items.
 * Auth-gated on `isAdmin === true`.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  getDocs,
  doc,
  getDoc,
  collection,
  collectionGroup,
  query,
  where,
  writeBatch,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { ArrowLeft, Tag } from 'lucide-react';
import { slugify } from '../utils/polishSlug';

/** Firestore commit cap is 500 writes per batch; pad to 450 for headroom. */
const BATCH_LIMIT = 450;

const ITEM_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'polish', label: 'Polish' },
  { value: 'plate', label: 'Plate' },
  { value: 'tool', label: 'Tool' },
  { value: 'powder', label: 'Powder' },
  { value: 'embellishment', label: 'Embellishment' },
  { value: 'transfer', label: 'Transfer / Decal' },
  { value: 'acrylic', label: 'Acrylic' },
  { value: 'cuticle_care', label: 'Cuticle Care' },
  { value: '', label: 'All types (no filter)' },
];

/** Which field the tool writes into. Polish-only fields are guarded
 *  below by auto-scoping the itemType filter to `polish` when picked.
 *  Mirrors the full structured-tag schema in types/index.ts so every
 *  category the polish AddModal exposes can also be pushed here. */
type TargetField =
  | 'tags'
  | 'baseType'
  | 'finish'
  | 'holoType'
  | 'colorShift'
  | 'particles'
  | 'specialFeatures'
  | 'colorFamily'
  | 'utility'
  | 'colorCharacter';

interface TargetOption {
  value: TargetField;
  label: string;
  polishOnly: boolean;
  /** `single` = writes a scalar string field (never overwrites when set).
   *  `array` = pushes into an array field with case-insensitive dedupe. */
  shape: 'array' | 'single';
}

const TARGET_OPTIONS: TargetOption[] = [
  // Generic — works for every itemType.
  { value: 'tags', label: 'Custom tags (generic — all item types)', polishOnly: false, shape: 'array' },
  // Polish-only structured fields — same order as the polish AddModal's
  // TagSelector so the mental model transfers. Singles (baseType,
  // colorShift) never overwrite an existing value; arrays dedupe on
  // insert.
  { value: 'baseType', label: 'Base Type (polish only, single value — creme, jelly, sheer, crelly)', polishOnly: true, shape: 'single' },
  { value: 'finish', label: 'Finish (polish only — shimmer, metallic, chrome, glossy, matte, etc.)', polishOnly: true, shape: 'array' },
  { value: 'holoType', label: 'Holo Type (polish only — linear holo, scattered holo, holo glitter)', polishOnly: true, shape: 'array' },
  { value: 'colorShift', label: 'Color Shift (polish only, single value — none, duochrome, multichrome)', polishOnly: true, shape: 'single' },
  { value: 'particles', label: 'Particles (polish only — glitter, flakes, aurora pigment, etc.)', polishOnly: true, shape: 'array' },
  { value: 'specialFeatures', label: 'Special Features (polish only — magnetic, thermal, solar, etc.)', polishOnly: true, shape: 'array' },
  { value: 'colorFamily', label: 'Color Family (polish only — red, blue, purple, etc.)', polishOnly: true, shape: 'array' },
  { value: 'utility', label: 'Utility (polish only — topper, undie, base coat, top coat, stamps well)', polishOnly: true, shape: 'array' },
  { value: 'colorCharacter', label: 'Color Character (polish only — bright, neon, pastel, vampy, etc.)', polishOnly: true, shape: 'array' },
];

interface PolishOption {
  slug: string;
  name: string;
  collectorCount: number;
}

export default function TagPusher() {
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);

  // === Step 1: scope ===
  const [itemTypeFilter, setItemTypeFilter] = useState<string>('polish');
  const [brandInput, setBrandInput] = useState('');
  /** Once a brand has been "loaded" (polishCatalog query fired), we
   *  remember which value it was for so the polish picker only shows
   *  polishes for that brand — typing more into brandInput without
   *  reloading doesn't stale the list. */
  const [loadedBrand, setLoadedBrand] = useState<string | null>(null);
  const [polishOptions, setPolishOptions] = useState<PolishOption[]>([]);
  const [loadingPolishes, setLoadingPolishes] = useState(false);
  /** Selected polish's slug (`${brandSlug}__${nameSlug}`) OR '' meaning
   *  "all polishes in the loaded brand". Only meaningful when
   *  itemTypeFilter === 'polish' AND a brand has been loaded. */
  const [selectedPolishSlug, setSelectedPolishSlug] = useState('');
  const [substring, setSubstring] = useState('');

  // === Step 2: what to add ===
  const [targetField, setTargetField] = useState<TargetField>('tags');
  const [tagsInput, setTagsInput] = useState('');

  // === Options ===
  const [alsoRemoveFromTags, setAlsoRemoveFromTags] = useState(false);
  const [dryRun, setDryRun] = useState(true);

  const [isRunning, setIsRunning] = useState(false);
  const [log, setLog] = useState<string[]>([]);

  const currentTarget: TargetOption =
    TARGET_OPTIONS.find((o) => o.value === targetField) ?? TARGET_OPTIONS[0];

  // Auto-scope itemType to polish when a polish-only target is picked.
  useEffect(() => {
    if (currentTarget.polishOnly && itemTypeFilter !== 'polish') {
      setItemTypeFilter('polish');
    }
  }, [targetField]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset polish selection when itemType shifts off polish — the
  // picker is meaningless for other types.
  useEffect(() => {
    if (itemTypeFilter !== 'polish') {
      setLoadedBrand(null);
      setPolishOptions([]);
      setSelectedPolishSlug('');
    }
  }, [itemTypeFilter]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user?.uid || null);
      if (user) {
        const snap = await getDoc(doc(db, 'users', user.uid));
        setIsAdmin(snap.exists() && snap.data()?.isAdmin === true);
      } else {
        setIsAdmin(false);
      }
      setCheckingAdmin(false);
    });
    return () => unsub();
  }, []);

  const say = (m: string) => {
    setLog((prev) => [...prev, m]);
    console.log('[TagPusher]', m);
  };

  /** Fetch polishCatalog docs for the typed brand so the polish picker
   *  can populate. Only useful when itemType === 'polish'. */
  const loadPolishesForBrand = async () => {
    const brand = brandInput.trim();
    if (!brand) {
      say('Type a brand name first, then click Load.');
      return;
    }
    const brandSlug = slugify(brand);
    setLoadingPolishes(true);
    setLog([]);
    setPolishOptions([]);
    setSelectedPolishSlug('');
    say(`Loading polishes for brand "${brand}" (brandSlug: ${brandSlug})…`);
    try {
      const snap = await getDocs(
        query(collection(db, 'polishCatalog'), where('brandSlug', '==', brandSlug))
      );
      const options: PolishOption[] = snap.docs
        .map((d) => {
          const data = d.data() as Record<string, unknown>;
          return {
            slug: d.id,
            name: typeof data.name === 'string' ? data.name : '',
            collectorCount:
              typeof data.collectorCount === 'number' ? data.collectorCount : 0,
          };
        })
        .filter((r) => r.name)
        .sort((a, b) => a.name.localeCompare(b.name));
      setPolishOptions(options);
      setLoadedBrand(brand);
      say(
        `Loaded ${options.length} polish${options.length === 1 ? '' : 'es'} for "${brand}". Pick one from the dropdown for a precise scope, or leave "All polishes in this brand" to hit every polish in the brand.`
      );
      if (options.length === 0) {
        say(
          `No polishCatalog entries for "${brand}". Double-check the spelling matches the brand's public name.`
        );
      }
    } catch (err) {
      say(`Load failed: ${(err as Error).message}`);
    } finally {
      setLoadingPolishes(false);
    }
  };

  /** Force the polishCatalog trigger to re-fire for the currently
   *  selected polish. Writes a `_lastAggregateTouch: serverTimestamp()`
   *  metadata field to one user item under that polish; the trigger's
   *  onWrite fires on any change, which re-aggregates the polishCatalog
   *  doc with the current trigger code's projection.
   *
   *  This is the fix path when the trigger was updated (e.g. widened
   *  to project specialFeatures/holoType/particles/etc.) but the
   *  polishCatalog doc has stale data because no user items have been
   *  written for that polish since the deploy. Common after tag-pusher
   *  runs where "already had value" outnumbered writes. */
  const rebuildPolishCatalog = async () => {
    if (!selectedPolishSlug) {
      say('Pick a specific polish from the dropdown first.');
      return;
    }
    setIsRunning(true);
    say(`Forcing polishCatalog rebuild for "${selectedPolishSlug}"…`);
    try {
      // Find ONE user item for this polish. Writing to one is enough —
      // the trigger recomputes the whole polishCatalog doc by re-scanning
      // every item under the slug, so one write is all we need to fire it.
      const snap = await getDocs(
        query(
          collectionGroup(db, 'items'),
          where('slug', '==', selectedPolishSlug)
        )
      );
      const userItemRef = snap.docs.find(
        (d) => d.ref.parent.parent?.parent?.id === 'users'
      )?.ref;
      if (!userItemRef) {
        say(
          `No user items found for slug "${selectedPolishSlug}". ` +
            `Can't force a trigger fire without at least one item to write to. ` +
            `(This is fine — it just means nobody owns this polish.)`
        );
        return;
      }
      await updateDoc(userItemRef, {
        _lastAggregateTouch: serverTimestamp(),
      });
      say(
        `Wrote _lastAggregateTouch on ${userItemRef.path}. The polishCatalog ` +
          `trigger will fire within a few seconds. Refresh the polish's detail ` +
          `page after ~5 seconds to see the re-aggregated data.`
      );
    } catch (err) {
      say(`Rebuild failed: ${(err as Error).message}`);
    } finally {
      setIsRunning(false);
    }
  };

  const run = async () => {
    const needle = substring.trim().toLowerCase();
    const typeFilter = itemTypeFilter.trim();
    const brandLowerFilter = brandInput.trim().toLowerCase();
    // Also compute a brandSlug for the same input so items whose
    // brandLower doesn't exactly match (e.g. brands with punctuation
    // like "Polish ALL the Things!") can still be matched via their
    // slugified brand identity.
    const brandSlugFilter = brandInput.trim() ? slugify(brandInput.trim()) : '';
    const usePolishSlug =
      typeFilter === 'polish' && !!selectedPolishSlug;

    // Guardrails so the scan isn't unbounded.
    if (!usePolishSlug && !needle && !brandLowerFilter && !typeFilter) {
      say(
        'Pick at least one scope: item type, brand, specific polish, or substring. Running with no scope would touch every item in the app.'
      );
      return;
    }
    if (!usePolishSlug && !needle && !brandLowerFilter) {
      say(
        'Add a brand, pick a polish, or type a substring — item type alone is too broad.'
      );
      return;
    }

    const explicitTags = tagsInput
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
    // If the user picked a polish AND left the tag input blank, we still
    // want SOMETHING to add — fall back to needle (substring) capitalized
    // OR complain if that's blank too.
    let tagsToAdd = explicitTags;
    if (tagsToAdd.length === 0) {
      if (needle) {
        tagsToAdd = [
          needle.charAt(0).toUpperCase() + needle.slice(1),
        ];
      } else {
        say('Enter at least one value to add.');
        return;
      }
    }

    setIsRunning(true);
    setLog([]);
    const doStripFromTags =
      alsoRemoveFromTags && currentTarget.value !== 'tags';
    say(
      `${dryRun ? '[DRY-RUN] ' : ''}Scanning…`
    );
    if (usePolishSlug) {
      const polishLabel =
        polishOptions.find((p) => p.slug === selectedPolishSlug)?.name ??
        selectedPolishSlug;
      say(`  Scope: single polish "${polishLabel}" (slug: ${selectedPolishSlug}).`);
    } else {
      if (typeFilter) say(`  itemType: ${typeFilter}`);
      if (brandLowerFilter) say(`  brand (exact on brandLower): ${brandLowerFilter}`);
      if (needle) say(`  substring in item name: "${needle}"`);
    }
    say(`  Target field: ${currentTarget.label}`);
    say(`  Values to add: [${tagsToAdd.join(', ')}]`);
    if (doStripFromTags) say(`  Also removing matching values from tags[].`);

    try {
      const snap = await getDocs(collectionGroup(db, 'items'));
      let scanned = 0;
      let skippedNonUser = 0;
      let skippedDeleted = 0;
      let skippedNoName = 0;
      let skippedNoMatch = 0;
      let skippedType = 0;
      let skippedBrand = 0;
      let skippedPolishSlug = 0;
      let skippedPolishGuard = 0;
      /** Counts items skipped because a single-value target field
       *  (baseType or colorShift) already has a value on the item.
       *  We never overwrite those — the user's setting wins. */
      let skippedSingleAlreadySet = 0;
      let alreadyTagged = 0;
      let queued = 0;
      let committed = 0;
      let stripQueued = 0;
      let batch = writeBatch(db);
      let ops = 0;

      const commitIfFull = async () => {
        if (ops >= BATCH_LIMIT) {
          if (!dryRun) await batch.commit();
          committed += ops;
          batch = writeBatch(db);
          ops = 0;
        }
      };

      for (const d of snap.docs) {
        scanned++;
        if (d.ref.parent.parent?.parent?.id !== 'users') {
          skippedNonUser++;
          continue;
        }
        const data = d.data() as Record<string, unknown>;
        if (data.isDeleted === true) {
          skippedDeleted++;
          continue;
        }

        // Precise polish scope — filter by canonical slug, skip everything else.
        if (usePolishSlug) {
          const itemSlug = typeof data.slug === 'string' ? data.slug : '';
          if (itemSlug !== selectedPolishSlug) {
            skippedPolishSlug++;
            continue;
          }
        }

        const name = typeof data.name === 'string' ? data.name.trim() : '';
        if (!name) {
          skippedNoName++;
          continue;
        }

        // Substring filter only applies when we're in the broad mode. In
        // single-polish scope, substring is ignored (the slug alone is
        // enough precision).
        if (!usePolishSlug && needle && !name.toLowerCase().includes(needle)) {
          skippedNoMatch++;
          continue;
        }

        const itemType =
          typeof data.itemType === 'string' ? data.itemType : 'polish';
        if (typeFilter && itemType !== typeFilter) {
          skippedType++;
          continue;
        }

        if (!usePolishSlug && brandLowerFilter) {
          const bl =
            typeof data.brandLower === 'string'
              ? data.brandLower
              : typeof data.brand === 'string'
              ? data.brand.trim().toLowerCase()
              : '';
          const bs =
            typeof data.brandSlug === 'string'
              ? data.brandSlug
              : typeof data.brand === 'string'
              ? slugify(data.brand)
              : '';
          // Match on EITHER brandLower or brandSlug — accepts items with
          // inconsistent brand fields (legacy items may only have one).
          if (bl !== brandLowerFilter && (!brandSlugFilter || bs !== brandSlugFilter)) {
            skippedBrand++;
            continue;
          }
        }

        // Polish-only target guard — a plate's finish[] doesn't mean
        // anything semantically.
        if (currentTarget.polishOnly && itemType !== 'polish') {
          skippedPolishGuard++;
          continue;
        }

        // Build the patch depending on target shape.
        const patch: Record<string, unknown> = {};

        if (currentTarget.shape === 'array') {
          const fieldKey = currentTarget.value;
          const raw = data[fieldKey];
          const existing = Array.isArray(raw)
            ? (raw as unknown[]).filter((t): t is string => typeof t === 'string')
            : [];
          const existingLower = new Set(existing.map((t) => t.toLowerCase()));
          const newValues = tagsToAdd.filter(
            (t) => !existingLower.has(t.toLowerCase())
          );
          if (newValues.length === 0) {
            if (!doStripFromTags) {
              alreadyTagged++;
              continue;
            }
          } else {
            patch[fieldKey] = [...existing, ...newValues];
          }
        } else {
          // Single-value field (baseType / colorShift). Only fill in when
          // empty; never overwrite a user-assigned value. Uses the
          // current target's key so the same branch handles both single
          // fields correctly.
          const fieldKey = currentTarget.value;
          const raw = data[fieldKey];
          const current = typeof raw === 'string' ? raw.trim() : '';
          if (current) {
            skippedSingleAlreadySet++;
            if (!doStripFromTags) continue;
          } else {
            patch[fieldKey] = tagsToAdd[0];
          }
        }

        if (doStripFromTags) {
          const rawTags = Array.isArray(data.tags)
            ? (data.tags as unknown[]).filter((t): t is string => typeof t === 'string')
            : [];
          const toStripLower = new Set(tagsToAdd.map((t) => t.toLowerCase()));
          const filtered = rawTags.filter(
            (t) => !toStripLower.has(t.toLowerCase())
          );
          if (filtered.length !== rawTags.length) {
            patch.tags = filtered;
            stripQueued++;
          }
        }

        if (Object.keys(patch).length === 0) {
          alreadyTagged++;
          continue;
        }

        if (!dryRun) batch.update(d.ref, patch);
        queued++;
        ops++;
        await commitIfFull();
      }

      if (ops > 0) {
        if (!dryRun) await batch.commit();
        committed += ops;
      }

      say(
        `${dryRun ? '(dry-run) ' : ''}Scan complete. ` +
          `Considered ${scanned} items (${skippedNonUser} non-user, ` +
          `${skippedDeleted} deleted, ${skippedNoName} unnamed, ` +
          (usePolishSlug ? `${skippedPolishSlug} not this polish, ` : `${skippedNoMatch} no substring match, `) +
          `${skippedType} type filter, ${skippedBrand} brand filter, ` +
          `${skippedPolishGuard} polish-only guard, ` +
          `${skippedSingleAlreadySet} single-value field already set, ${alreadyTagged} already had value). ` +
          `${dryRun ? 'Would write' : 'Wrote'}: ${queued} items` +
          `${dryRun ? '' : ` (${committed} writes committed)`}.` +
          (doStripFromTags
            ? ` Tags[] stripped on ${stripQueued} of those.`
            : '')
      );
    } catch (err) {
      say(`Failed: ${(err as Error).message}`);
    } finally {
      setIsRunning(false);
    }
  };

  if (checkingAdmin) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-glass-secondary">
        Checking access…
      </div>
    );
  }
  if (!currentUser) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-glass-secondary">
        Please sign in to use this admin tool.
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-glass-secondary">
        This page is admin-only.
      </div>
    );
  }

  const brandLoadedForCurrentInput =
    loadedBrand !== null && loadedBrand.trim() === brandInput.trim();

  return (
    <div className="max-w-3xl mx-auto p-6">
      <Link
        to="/admin"
        className="inline-flex items-center gap-1 text-glass-muted hover:text-glass mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Admin
      </Link>

      <h1 className="text-2xl font-bold text-glass mb-2 inline-flex items-center gap-2">
        <Tag className="w-6 h-6" /> Tag Pusher
      </h1>
      <p className="text-glass-secondary text-sm mb-6">
        Add a value to a chosen field on user items. Pick your scope
        first (item type, brand, optionally a specific polish), then
        pick the value + target field. Non-destructive: item names are
        never modified; only the chosen field gets the new value
        appended (deduped).
      </p>

      {/* === STEP 1: SCOPE === */}
      <div className="bg-slate-800/40 border border-slate-600 rounded-lg p-4 mb-4 space-y-3">
        <h2 className="text-sm font-semibold text-glass">
          Step 1 · Scope <span className="text-glass-muted font-normal">(what items will be touched)</span>
        </h2>

        <div>
          <label className="block text-xs text-glass-muted mb-1">
            Item type
          </label>
          <select
            value={itemTypeFilter}
            onChange={(e) => setItemTypeFilter(e.target.value)}
            disabled={isRunning || currentTarget.polishOnly}
            className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-glass disabled:opacity-60"
          >
            {ITEM_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {currentTarget.polishOnly && (
            <div className="text-[10px] text-glass-muted mt-1">
              Locked to Polish because the target field is polish-only.
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs text-glass-muted mb-1">
            Brand
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={brandInput}
              onChange={(e) => setBrandInput(e.target.value)}
              onKeyDown={(e) => {
                if (
                  e.key === 'Enter' &&
                  itemTypeFilter === 'polish' &&
                  brandInput.trim()
                ) {
                  loadPolishesForBrand();
                }
              }}
              placeholder="e.g. Mooncat, Essie, Loaded Lacquer"
              disabled={isRunning}
              className="flex-1 bg-slate-900 border border-slate-600 rounded px-3 py-2 text-glass"
            />
            {itemTypeFilter === 'polish' && (
              <button
                onClick={loadPolishesForBrand}
                disabled={isRunning || loadingPolishes || !brandInput.trim()}
                className="px-3 py-2 bg-slate-700 text-glass rounded hover:bg-slate-600 disabled:opacity-50 whitespace-nowrap"
              >
                {loadingPolishes
                  ? 'Loading…'
                  : brandLoadedForCurrentInput
                  ? 'Reload'
                  : 'Load polishes'}
              </button>
            )}
          </div>
          <div className="text-[10px] text-glass-muted mt-1">
            Matches items whose <code>brandLower</code> equals this value
            (case-insensitive). Leave blank to skip the brand filter.
          </div>
        </div>

        {itemTypeFilter === 'polish' && brandLoadedForCurrentInput && (
          <div>
            <label className="block text-xs text-glass-muted mb-1">
              Polish {polishOptions.length > 0 && `(${polishOptions.length} available)`}
            </label>
            <select
              value={selectedPolishSlug}
              onChange={(e) => setSelectedPolishSlug(e.target.value)}
              disabled={isRunning || polishOptions.length === 0}
              className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-glass disabled:opacity-60"
            >
              <option value="">
                — All polishes in this brand —
              </option>
              {polishOptions.map((p) => (
                <option key={p.slug} value={p.slug}>
                  {p.name} ({p.collectorCount} collector
                  {p.collectorCount === 1 ? '' : 's'})
                </option>
              ))}
            </select>
            {selectedPolishSlug && (
              <>
                <div className="text-[10px] text-glass-muted mt-1">
                  Precise mode: only items with this polish's exact slug
                  will be touched. Substring below is ignored.
                </div>
                <div className="mt-2 flex items-start gap-2">
                  <button
                    onClick={rebuildPolishCatalog}
                    disabled={isRunning}
                    className="px-3 py-1.5 text-xs bg-slate-700 text-glass rounded hover:bg-slate-600 disabled:opacity-50"
                    title="Writes a metadata field to one user item under this polish. Fires the polishCatalog trigger which re-aggregates the polish's chip data with the current trigger code's projection."
                  >
                    Rebuild polishCatalog for this polish
                  </button>
                  <span className="text-[10px] text-glass-muted mt-1 flex-1">
                    Use this if you pushed a tag but the detail page's
                    chip strip is still missing it — a Tag Pusher run
                    that ends in "already had value" doesn't fire the
                    aggregation trigger. Nothing on the item's visible
                    fields changes.
                  </span>
                </div>
              </>
            )}
          </div>
        )}

        <div>
          <label className="block text-xs text-glass-muted mb-1">
            Substring to match in item name{' '}
            <span className="text-glass-muted">(optional)</span>
          </label>
          <input
            type="text"
            value={substring}
            onChange={(e) => setSubstring(e.target.value)}
            placeholder="e.g. magnetic, thermal, holo — case-insensitive"
            disabled={isRunning || !!selectedPolishSlug}
            className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-glass disabled:opacity-60"
          />
          <div className="text-[10px] text-glass-muted mt-1">
            {selectedPolishSlug
              ? 'Disabled — a specific polish is picked above.'
              : 'Broad match on the item name. Combines with brand + item type filters.'}
          </div>
        </div>
      </div>

      {/* === STEP 2: WHAT TO ADD === */}
      <div className="bg-slate-800/40 border border-slate-600 rounded-lg p-4 mb-4 space-y-3">
        <h2 className="text-sm font-semibold text-glass">
          Step 2 · What to add
        </h2>

        <div>
          <label className="block text-xs text-glass-muted mb-1">
            Target field
          </label>
          <select
            value={targetField}
            onChange={(e) => setTargetField(e.target.value as TargetField)}
            disabled={isRunning}
            className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-glass"
          >
            {TARGET_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs text-glass-muted mb-1">
            Value(s) to add — comma-separated. Blank auto-derives from the
            substring (capitalized).
          </label>
          <input
            type="text"
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            placeholder="e.g. Magnetic"
            disabled={isRunning}
            className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-glass"
          />
          {currentTarget.shape === 'single' &&
            tagsInput.split(',').filter((t) => t.trim()).length > 1 && (
              <div className="text-[10px] text-amber-300 mt-1">
                Single-value field — only the first entry will be used.
              </div>
            )}
        </div>
      </div>

      {/* === OPTIONS + RUN === */}
      <div className="bg-slate-800/40 border border-slate-600 rounded-lg p-4 mb-4 space-y-3">
        {currentTarget.value !== 'tags' && (
          <label className="flex items-start gap-2 text-glass text-sm">
            <input
              type="checkbox"
              checked={alsoRemoveFromTags}
              onChange={(e) => setAlsoRemoveFromTags(e.target.checked)}
              disabled={isRunning}
              className="mt-1"
            />
            <span>
              Also remove the matching value from <code>tags[]</code> on each
              affected item — use this to migrate items you previously pushed
              into <code>tags[]</code> that actually belong in this structured
              field.
            </span>
          </label>
        )}
        <label className="flex items-center gap-2 text-glass text-sm">
          <input
            type="checkbox"
            checked={dryRun}
            onChange={(e) => setDryRun(e.target.checked)}
            disabled={isRunning}
          />
          Dry-run (preview counts without writing)
        </label>
        <button
          onClick={run}
          disabled={isRunning}
          className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
        >
          {isRunning ? 'Working…' : dryRun ? 'Preview' : 'Add tags'}
        </button>
      </div>

      {log.length > 0 && (
        <div className="bg-slate-900 border border-slate-700 rounded-lg p-4">
          <h3 className="text-glass-secondary text-sm mb-2">Log</h3>
          <pre className="text-xs text-glass-muted whitespace-pre-wrap">
            {log.join('\n')}
          </pre>
        </div>
      )}
    </div>
  );
}
